import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { AdminLayout } from '@/components/layouts/AdminLayout';
import { Monitor, Download, Printer, Wifi, WifiOff, ExternalLink, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from "@/utils/api";

const AdminQRDisplay = () => {
    const [displayUrl, setDisplayUrl] = useState('');
    const [copied, setCopied] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState("");

    // Cargar sucursales
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                // Importar API URL dinámicamente o usar hardcoded relativo si es SPA
                // Mejor usar la instancia de axios configurada si es posible, o fetch directo
                const response = await fetch(`${API_URL}/public/branches`);
                if (response.ok) {
                    const data = await response.json();
                    setBranches(data);
                    // Seleccionar la primera por defecto si hay
                    if (data.length > 0) setSelectedBranch(data[0].branch_id);
                }
            } catch (error) {
                console.error("Error loading branches", error);
            }
        };
        fetchBranches();
    }, []);

    useEffect(() => {
        // Construir la URL completa
        const protocol = window.location.protocol;
        const hostname = window.location.hostname;
        const port = window.location.port;
        const baseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}`;

        let fullUrl = `${baseUrl}/pedidos-local`;
        if (selectedBranch) {
            fullUrl += `?branch=${selectedBranch}`;
        }

        setDisplayUrl(fullUrl);

        // Monitor online status
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [selectedBranch]);

    const handleDownloadQR = () => {
        const svg = document.getElementById('qr-code-svg');
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const pngFile = canvas.toDataURL('image/png');

            const downloadLink = document.createElement('a');
            downloadLink.download = 'pantalla-pedidos-qr.png';
            downloadLink.href = pngFile;
            downloadLink.click();

            toast.success('✅ Código QR descargado');
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    };

    const handlePrintQR = () => {
        const printWindow = window.open('', '_blank');
        const qrSize = 400;

        printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Código QR - Pantalla de Pedidos</title>
          <style>
            @page { margin: 2cm; }
            body {
              font-family: 'Arial', sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              padding: 20px;
            }
            .container {
              text-align: center;
              max-width: 600px;
            }
            h1 {
              color: #16a34a;
              font-size: 32px;
              margin-bottom: 10px;
            }
            p {
              color: #666;
              font-size: 18px;
              margin-bottom: 30px;
            }
            .qr-container {
              background: white;
              padding: 30px;
              border-radius: 15px;
              box-shadow: 0 4px 6px rgba(0,0,0,0.1);
              margin: 20px 0;
            }
            .url {
              background: #f3f4f6;
              padding: 15px;
              border-radius: 8px;
              margin-top: 20px;
              font-size: 14px;
              color: #374151;
              word-break: break-all;
            }
            .instructions {
              margin-top: 30px;
              text-align: left;
              padding: 20px;
              background: #f9fafb;
              border-radius: 10px;
            }
            .instructions h2 {
              color: #111827;
              font-size: 20px;
              margin-bottom: 15px;
            }
            .instructions ol {
              color: #374151;
              line-height: 1.8;
            }
            .instructions li {
              margin-bottom: 10px;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>🖥️ Pantalla de Pedidos</h1>
            <p>Escanea este código QR para ver los pedidos en tiempo real</p>
            
            <div class="qr-container">
              ${document.getElementById('qr-code-svg').outerHTML}
            </div>
            
            <div class="url">
              <strong>URL:</strong> ${displayUrl}
            </div>
            
            <div class="instructions">
              <h2>📱 Instrucciones de uso:</h2>
              <ol>
                <li><strong>Escanea</strong> el código QR con tu smartphone o tablet</li>
                <li><strong>Asegúrate</strong> de estar conectado a la misma red WiFi</li>
                <li><strong>Pantalla completa:</strong> Presiona F11 o usa el botón de pantalla completa</li>
                <li><strong>La pantalla</strong> se actualiza automáticamente cada 5 segundos</li>
              </ol>
            </div>
          </div>
          <script>
            setTimeout(() => window.print(), 500);
          </script>
        </body>
      </html>
    `);
        printWindow.document.close();
    };

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(displayUrl);
            setCopied(true);
            toast.success('✅ URL copiada al portapapeles');
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast.error('❌ Error al copiar URL');
        }
    };

    const handleOpenDisplay = () => {
        window.open(displayUrl, '_blank', 'width=1920,height=1080');
        toast.success('🖥️ Pantalla de pedidos abierta');
    };

    return (
        <AdminLayout>
            <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 p-6">
                {/* Header con estado de conexión */}
                <div className="max-w-4xl mx-auto mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-4xl font-bold text-gray-900 mb-2">
                                🖥️ Pantalla de Pedidos - QR
                            </h1>
                            <p className="text-gray-600">
                                Genera y comparte el código QR para ver los pedidos en cualquier dispositivo
                            </p>
                        </div>
                        <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${isOnline
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                            }`}>
                            {isOnline ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
                            <span className="font-semibold">
                                {isOnline ? 'Online' : 'Offline'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
                    {/* Columna izquierda - QR Code */}
                    <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">
                                Código QR
                            </h2>
                            <p className="text-gray-600 mb-6">
                                Escanea con tu smartphone
                            </p>

                            {/* Branch Selector */}
                            {branches.length > 0 && (
                                <div className="mb-6 relative">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2 text-left">
                                        Seleccionar Sucursal
                                    </label>
                                    <select
                                        value={selectedBranch}
                                        onChange={(e) => setSelectedBranch(e.target.value)}
                                        className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium text-gray-800 shadow-sm outline-none appearance-none cursor-pointer"
                                    >
                                        {branches.map(branch => (
                                            <option key={branch.branch_id} value={branch.branch_id}>
                                                {branch.name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute right-4 top-[38px] pointer-events-none text-gray-500 text-xs">
                                        ▼
                                    </div>
                                </div>
                            )}

                            {/* QR Code */}
                            <div className="bg-gradient-to-br from-green-100 to-emerald-100 p-8 rounded-xl mb-6 flex items-center justify-center">
                                <div className="bg-white p-6 rounded-lg shadow-lg">
                                    <QRCodeSVG
                                        id="qr-code-svg"
                                        value={displayUrl}
                                        size={280}
                                        level="H"
                                        includeMargin={true}
                                        imageSettings={{
                                            src: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMjEgMTZWOEw1IDhWMTlMOSAxOSIgc3Ryb2tlPSIjMTZhMzRhIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8cGF0aCBkPSJNMTcgOFY1TDcgNVY4IiBzdHJva2U9IiMxNmEzNGEiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik0xMyAxNkgyMVYyMEgxM1YxNloiIGZpbGw9IiMxNmEzNGEiLz4KPC9zdmc+Cg==",
                                            height: 40,
                                            width: 40,
                                            excavate: true,
                                        }}
                                    />
                                </div>
                            </div>

                            {/* URL Display */}
                            <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
                                <p className="text-xs text-gray-500 mb-2 font-semibold">URL de la pantalla:</p>
                                <div className="flex items-center gap-2">
                                    <p className="text-sm text-gray-700 font-mono flex-1 truncate">
                                        {displayUrl}
                                    </p>
                                    <button
                                        onClick={handleCopyUrl}
                                        className={`p-2 rounded-lg transition-all ${copied
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                        title="Copiar URL"
                                    >
                                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="space-y-3">
                                <button
                                    onClick={handleDownloadQR}
                                    className="w-full flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition-colors font-semibold shadow-lg hover:shadow-xl"
                                >
                                    <Download className="w-5 h-5" />
                                    Descargar QR (PNG)
                                </button>

                                <button
                                    onClick={handlePrintQR}
                                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors font-semibold shadow-lg hover:shadow-xl"
                                >
                                    <Printer className="w-5 h-5" />
                                    Imprimir QR
                                </button>

                                <button
                                    onClick={handleOpenDisplay}
                                    className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-xl hover:bg-purple-700 transition-colors font-semibold shadow-lg hover:shadow-xl"
                                >
                                    <ExternalLink className="w-5 h-5" />
                                    Abrir Pantalla
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Columna derecha - Instrucciones */}
                    <div className="space-y-6">
                        {/* Instrucciones de uso */}
                        <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Monitor className="w-6 h-6 text-green-600" />
                                ¿Cómo usar el QR?
                            </h3>
                            <div className="space-y-4">
                                <div className="flex gap-3">
                                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold">
                                        1
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-1">Escanea el QR</h4>
                                        <p className="text-sm text-gray-600">
                                            Usa la cámara de tu smartphone o tablet para escanear el código QR
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold">
                                        2
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-1">Misma red WiFi</h4>
                                        <p className="text-sm text-gray-600">
                                            Asegúrate de estar conectado a la misma red que el servidor
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold">
                                        3
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-1">Pantalla completa</h4>
                                        <p className="text-sm text-gray-600">
                                            Presiona F11 o usa el botón de pantalla completa del navegador
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold">
                                        4
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-1">Actualización automática</h4>
                                        <p className="text-sm text-gray-600">
                                            La pantalla se actualiza cada 5 segundos mostrando los pedidos en tiempo real
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tips y recomendaciones */}
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-xl p-6 border border-amber-200">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">
                                💡 Tips y Recomendaciones
                            </h3>
                            <ul className="space-y-3 text-sm text-gray-700">
                                <li className="flex items-start gap-2">
                                    <span className="text-green-600 font-bold">✓</span>
                                    <span><strong>Imprime el QR</strong> y colócalo en un lugar visible para los clientes</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-600 font-bold">✓</span>
                                    <span><strong>TV o monitor:</strong> Conecta un dispositivo y deja la pantalla abierta permanentemente</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-600 font-bold">✓</span>
                                    <span><strong>Tabletas:</strong> Ideal para mostrar en mostradores o mesas</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-600 font-bold">✓</span>
                                    <span><strong>Múltiples pantallas:</strong> Puedes abrir la URL en varios dispositivos simultáneamente</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-amber-600 font-bold">⚠</span>
                                    <span><strong>Red local:</strong> Funciona solo en la misma red WiFi del restaurante</span>
                                </li>
                            </ul>
                        </div>

                        {/* Info técnica */}
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                            <p className="text-xs text-gray-500 mb-2">
                                <strong>Información técnica:</strong>
                            </p>
                            <ul className="text-xs text-gray-600 space-y-1">
                                <li>• Actualización: Cada 5 segundos</li>
                                <li>• Estados mostrados: Pendiente, Preparando, Listo</li>
                                <li>• No requiere autenticación</li>
                                <li>• Funciona en cualquier dispositivo con navegador</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminQRDisplay;
